/**
 * API module for SaaS platform supervision.
 *
 * This file is the single frontend access point for /platform/monitoring/*
 * endpoints. Pages must import this module instead of calling api.get directly.
 */

import { api } from "@/lib/api";

/**
 * Fetch global platform counters and service health.
 */
export const overview = () =>
  api.get("/platform/monitoring/overview").then((response) => response.data);

/**
 * Fetch read-only system metrics visible from the backend container.
 */
export const system = () =>
  api.get("/platform/monitoring/system").then((response) => response.data);

/**
 * Fetch aggregated security indicators.
 */
export const security = () =>
  api.get("/platform/monitoring/security").then((response) => response.data);

/**
 * Fetch RGPD supervision indicators.
 */
export const rgpd = () =>
  api.get("/platform/monitoring/rgpd").then((response) => response.data);


/**
 * Login dedicated to the SaaS Admin supervision entrypoint.
 *
 * This still uses the central auth endpoint, but the page checks that
 * the returned user role is exactly "saas_admin".
 */
export const loginSaasAdmin = (email, password) =>
  api.post("/auth/platform-login", { email, password }).then((response) => response.data);

/**
 * Fetch global accounts overview: tenants + users.
 */
export const accounts = () =>
  api.get("/platform/monitoring/accounts").then((response) => response.data);

/**
 * Fetch platform audit logs.
 */
export const logs = (params = {}) =>
  api.get("/platform/monitoring/logs", { params }).then((response) => response.data);

/**
 * Update tenant active/suspended status.
 */
export const updateTenantStatus = (tenantId, status) =>
  api.patch(`/platform/monitoring/accounts/tenants/${tenantId}/status`, { status }).then((response) => response.data);

/**
 * Update user active/disabled status.
 */
export const updateUserStatus = (userId, status) =>
  api.patch(`/platform/monitoring/accounts/users/${userId}/status`, { status }).then((response) => response.data);

/**
 * Update user role.
 */
export const updateUserRole = (userId, role) =>
  api.patch(`/platform/monitoring/accounts/users/${userId}/role`, { role }).then((response) => response.data);

/**
 * Update tenant metadata.
 */
export const updateTenant = (tenantId, payload) =>
  api.patch(`/platform/monitoring/accounts/tenants/${tenantId}`, payload).then((response) => response.data);

/**
 * Create a platform user.
 */
export const createUser = (payload) =>
  api.post("/platform/monitoring/accounts/users", payload).then((response) => response.data);

/**
 * Update user metadata.
 */
export const updateUser = (userId, payload) =>
  api.patch(`/platform/monitoring/accounts/users/${userId}`, payload).then((response) => response.data);

/**
 * Reset user password.
 */
export const resetUserPassword = (userId) =>
  api.post(`/platform/monitoring/accounts/users/${userId}/reset-password`).then((response) => response.data);

/**
 * Fetch paginated accounts history.
 */
export const accountsPaginated = (params = {}) =>
  api.get("/platform/monitoring/accounts/paginated", { params }).then((response) => response.data);

/**
 * Fetch paginated audit logs history.
 */
export const logsPaginated = (params = {}) =>
  api.get("/platform/monitoring/logs/paginated", { params }).then((response) => response.data);

/**
 * Fetch RGPD admin dashboard.
 */
export const rgpdAdmin = (params = {}) =>
  api.get("/platform/monitoring/rgpd/admin", { params }).then((response) => response.data);

/**
 * Export user RGPD data.
 */
export const exportRgpdUser = (userId) =>
  api.post(`/platform/monitoring/rgpd/export-user/${userId}`).then((response) => response.data);

/**
 * Export tenant RGPD inventory.
 */
export const exportRgpdTenant = (tenantId) =>
  api.post(`/platform/monitoring/rgpd/export-tenant/${tenantId}`).then((response) => response.data);

/**
 * Anonymize a user.
 */
export const anonymizeRgpdUser = (userId, payload) =>
  api.post(`/platform/monitoring/rgpd/anonymize-user/${userId}`, payload).then((response) => response.data);

/**
 * Anonymize a prospect.
 */
export const anonymizeRgpdProspect = (prospectId, payload) =>
  api.post(`/platform/monitoring/rgpd/anonymize-prospect/${prospectId}`, payload).then((response) => response.data);

/**
 * Update RGPD retention settings.
 */
export const updateRgpdRetention = (payload) =>
  api.patch("/platform/monitoring/rgpd/retention", payload).then((response) => response.data);

/**
 * Delete a user on RGPD request.
 */
export const deleteRgpdUser = (userId, payload) =>
  api.delete(`/platform/monitoring/rgpd/delete-user/${userId}`, { data: payload }).then((response) => response.data);

/**
 * Reset tenant statistics without deleting business data.
 */
export const resetTenantStats = (tenantId, payload) =>
  api.post(`/platform/monitoring/tenants/${tenantId}/reset-stats`, payload).then((response) => response.data);

/**
 * Fetch platform prospects for SaaS Admin RGPD actions.
 */
export const prospects = (params = {}) =>
  api.get("/platform/monitoring/prospects", { params }).then((response) => response.data);

/**
 * Delete a user with a standard platform admin action.
 */
export const deleteUser = (userId, payload) =>
  api.delete(`/platform/monitoring/accounts/users/${userId}`, { data: payload }).then((response) => response.data);

/**
 * Export prospect RGPD data.
 */
export const exportRgpdProspect = (prospectId) =>
  api.post(`/platform/monitoring/rgpd/export-prospect/${prospectId}`).then((response) => response.data);

/**
 * Delete a prospect on RGPD request.
 */
export const deleteRgpdProspect = (prospectId, payload) =>
  api.delete(`/platform/monitoring/rgpd/delete-prospect/${prospectId}`, { data: payload }).then((response) => response.data);

/**
 * Delete a prospect with a standard platform admin action.
 */
export const deleteProspect = (prospectId, payload) =>
  api.delete(`/platform/monitoring/accounts/prospects/${prospectId}`, { data: payload }).then((response) => response.data);

export const unlockUser = (userId) =>
  api.post(`/platform/accounts/users/${userId}/unlock`).then((response) => response.data);
